import React from "react";

import {
  faCloudUploadAlt,
  faFileDownload,
} from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Paper,
  Step,
  StepButton,
  StepLabel,
  Stepper,
} from "@material-ui/core";
import Container from "@material-ui/core/Container";
import { useDispatch, useSelector } from "react-redux";

import { importFile, processImportFile, updateFileImport } from "actions";

import { Button, DeleteButton, IconButton } from "components/Form";
import { CheckBoxBase } from "components/Form/FormikControls";
import { UploadIcon } from "components/Importer/components";
import { FileImportField } from "components/Importer/FileImportField";
import { Row } from "components/Layout";

import { TranslatedSaleTypeLabel } from "containers/TranslatedSaleTypeLabel";

import { openOverview } from "lib/navigation";

import { getConsignments } from "selectors";

import { MapBuyerTable, MapVendorTable } from "./BusinessImportComponents";
import { MapProductTable } from "./ProductImportComponents";
import { MapRoundTable } from "./RoundImportComponents";

const ImportTab = {
  ACKNOWLEDGEMENT: 0,
  SELECT_FILE: 1,
  VENDORS: 2,
  PRODUCTS: 3,
  BUYERS: 4,
  SALE_ROUNDS: 5,
  UPLOAD: 6,
};

export const GenericMultiFileTypeImporterSection = {
  BUYERS: "BUYERS",
  PRODUCTS: "PRODUCTS",
  SALE_ROUNDS: "SALE_ROUNDS",
  VENDORS: "VENDORS",
};

const ImportContentAcknowledgement = ({
  allowKeepData = false,
  replaceData,
  setReplaceData,
  readOnly,
}) => {
  const informationText = allowKeepData
    ? "Importing data from the file you provide will add new information into the sale including:"
    : "Importing a sale will replace all existing information in the sale including:";

  const keepDataText =
    "If unchecked, existing content will be kept in this Sale. This could result in duplicate data if importing identical information.";
  const replaceDataText =
    "If checked, all sale content will be removed and replaced by new imported data.";

  return (
    <>
      <h2>Import content into this Sale</h2>
      <p>{informationText}</p>
      <ul>
        <li>Consignments</li>
        <li>Media and Attachments (including NVDs)</li>
        <li>Sale Lots</li>
        <li>Scans</li>
      </ul>
      {allowKeepData && !readOnly && (
        <>
          <Row>
            <CheckBoxBase
              label="Replace all existing data"
              onChange={() => setReplaceData(!replaceData)}
              value={replaceData}
            />
          </Row>
          <em className="text-grey">
            {replaceData ? replaceDataText : keepDataText}
          </em>
        </>
      )}
    </>
  );
};

export function GenericMultiFileTypeImporter({
  importName,
  fileTypes,
  fileDescription,
  type,
  sections = [],
  HelpTextComponent,
  allowKeepData = false,
}) {
  const dispatch = useDispatch();

  const [replaceData, setReplaceData] = React.useState(!allowKeepData || false);

  const fileState = useSelector(state => state?.importers?.[importName]);
  const {
    buyers,
    file: existingFile,
    isDataLossAcknowledged,
    isImportRunning,
    selectedTab,
    products,
    rounds,
    vendors,
  } = fileState;

  const isUsingBuyersSection = sections.includes(
    GenericMultiFileTypeImporterSection.BUYERS,
  );
  const isUsingProductsSection = sections.includes(
    GenericMultiFileTypeImporterSection.PRODUCTS,
  );
  const isUsingSaleRoundsSection = sections.includes(
    GenericMultiFileTypeImporterSection.SALE_ROUNDS,
  );
  const isUsingVendorsSection = sections.includes(
    GenericMultiFileTypeImporterSection.VENDORS,
  );

  const vendorsCount = vendors?.length || 0;
  const mappedVendorsCount =
    vendors?.filter(vendorMapping => vendorMapping.vendorId).length || 0;
  const productsCount = products?.length || 0;
  const mappedProductsCount =
    products?.filter(productMapping => productMapping.productId).length || 0;

  const buyersCount = buyers?.length || 0;
  const mappedBuyersCount =
    buyers?.filter(buyerMapping => buyerMapping.buyerId).length || 0;

  const roundsCount = rounds?.length || 0;
  const mappedRoundsCount =
    rounds?.filter(roundMapping => roundMapping.roundId).length || 0;

  const { name, sale } = existingFile || {};
  const isImportEnabled =
    isDataLossAcknowledged &&
    sale &&
    (!isUsingProductsSection || mappedProductsCount === productsCount) &&
    (!isUsingVendorsSection || mappedVendorsCount === vendorsCount) &&
    (!isUsingBuyersSection || mappedBuyersCount === buyersCount) &&
    (!isUsingSaleRoundsSection || mappedRoundsCount === roundsCount);

  const consignments = useSelector(getConsignments);
  const consignmentsCount = Object.values(consignments).length;

  function setDataLossAcknowledged() {
    dispatch(
      updateFileImport(
        {
          isDataLossAcknowledged: true,
          selectedTab: ImportTab.SELECT_FILE,
        },
        type,
      ),
    );
  }

  function selectTab(selectedTab) {
    if (isDataLossAcknowledged) {
      dispatch(
        updateFileImport(
          {
            selectedTab,
          },
          type,
        ),
      );
    }
  }

  return (
    <>
      <Paper square>
        <Stepper nonLinear activeStep={selectedTab} alternativeLabel>
          <Step key={ImportTab.ACKNOWLEDGEMENT}>
            <StepButton
              completed={isDataLossAcknowledged}
              onClick={() => selectTab(ImportTab.ACKNOWLEDGEMENT)}
            >
              Acknowledgement
            </StepButton>
          </Step>
          <Step key={ImportTab.SELECT_FILE}>
            <StepButton
              disabled={!isDataLossAcknowledged}
              completed={isDataLossAcknowledged && Boolean(existingFile)}
              onClick={() => selectTab(ImportTab.SELECT_FILE)}
              data-tour="selectFile"
            >
              {existingFile ? `${name} Selected` : "Select File"}
            </StepButton>
          </Step>
          {isUsingVendorsSection && (
            <Step key={ImportTab.VENDORS}>
              <StepButton
                optional={`${mappedVendorsCount}/${vendorsCount}`}
                onClick={() => selectTab(ImportTab.VENDORS)}
                completed={
                  isDataLossAcknowledged &&
                  sale &&
                  mappedVendorsCount === vendorsCount
                }
                disabled={!isDataLossAcknowledged || !sale}
                data-tour="mapVendors"
              >
                Map Vendors
              </StepButton>
            </Step>
          )}

          {isUsingProductsSection && (
            <Step key={ImportTab.PRODUCTS}>
              <StepButton
                optional={`${mappedProductsCount}/${productsCount}`}
                onClick={() => selectTab(ImportTab.PRODUCTS)}
                completed={
                  isDataLossAcknowledged &&
                  sale &&
                  mappedProductsCount === productsCount
                }
                disabled={!isDataLossAcknowledged || !sale}
                data-tour="mapProducts"
              >
                Map Products
              </StepButton>
            </Step>
          )}
          {isUsingBuyersSection && (
            <Step key={ImportTab.BUYERS}>
              <StepButton
                optional={`${mappedBuyersCount}/${buyersCount}`}
                onClick={() => selectTab(ImportTab.BUYERS)}
                completed={
                  isDataLossAcknowledged &&
                  sale &&
                  mappedBuyersCount === buyersCount
                }
                disabled={!isDataLossAcknowledged || !sale}
                data-tour="mapBuyers"
              >
                Map Buyers
              </StepButton>
            </Step>
          )}

          {isUsingSaleRoundsSection && (
            <Step key={ImportTab.SALE_ROUNDS}>
              <StepButton
                optional={`${mappedRoundsCount}/${roundsCount}`}
                onClick={() => selectTab(ImportTab.SALE_ROUNDS)}
                completed={
                  isDataLossAcknowledged &&
                  sale &&
                  mappedRoundsCount === roundsCount
                }
                disabled={!isDataLossAcknowledged || !sale}
                data-tour="mapRounds"
              >
                Map Rounds
              </StepButton>
            </Step>
          )}

          <Step key={ImportTab.UPLOAD}>
            {isImportEnabled ? (
              isImportRunning ? (
                <StepButton icon={<CircularProgress />}>Importing</StepButton>
              ) : (
                <StepButton
                  icon={<UploadIcon icon={faCloudUploadAlt} />}
                  onClick={() => dispatch(importFile(type, replaceData))}
                  data-tour="importSaleData"
                >
                  Import Sale Data
                </StepButton>
              )
            ) : (
              <StepLabel error>Import Sale Data</StepLabel>
            )}
          </Step>
        </Stepper>
      </Paper>
      {selectedTab === ImportTab.ACKNOWLEDGEMENT && (
        <Container>
          <Card>
            <CardContent>
              <ImportContentAcknowledgement
                allowKeepData={allowKeepData}
                replaceData={replaceData}
                setReplaceData={setReplaceData}
                readOnly={consignmentsCount === 0}
              />
              <CardActions>
                <Button onClick={() => openOverview()}>
                  Return to <TranslatedSaleTypeLabel label="Overview" />
                </Button>
                <DeleteButton
                  onClick={() => {
                    setDataLossAcknowledged();
                  }}
                  data-tour="progressToImport"
                >
                  Progress to Import
                </DeleteButton>
              </CardActions>
            </CardContent>
          </Card>
        </Container>
      )}
      {selectedTab === ImportTab.SELECT_FILE && (
        <>
          {type.DOWNLOAD_SAMPLE && (
            <Row>
              <p className="text-left">
                <IconButton
                  icon={faFileDownload}
                  onClick={() =>
                    dispatch({ type: type.DOWNLOAD_SAMPLE.REQUEST })
                  }
                  type="button"
                >
                  Download Template File
                </IconButton>
              </p>
              <HelpTextComponent />
            </Row>
          )}
          <FileImportField
            accept={fileTypes}
            existingFileName={name}
            fileDescription={fileDescription}
            processFile={(file, fileContents) =>
              dispatch(processImportFile(file, fileContents, type))
            }
          />
        </>
      )}
      {selectedTab === ImportTab.VENDORS && (
        <MapVendorTable importName={importName} type={type} />
      )}
      {selectedTab === ImportTab.PRODUCTS && (
        <MapProductTable importName={importName} type={type} />
      )}
      {selectedTab === ImportTab.BUYERS && (
        <MapBuyerTable importName={importName} type={type} />
      )}
      {selectedTab === ImportTab.SALE_ROUNDS && (
        <MapRoundTable importName={importName} type={type} />
      )}
    </>
  );
}
